import _ from 'lodash';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { NotUndefined } from '../../general.types';
import { useComponentIsStillLoaded } from '../misc-hooks';
import {
  retrieveStoredItem,
  storeItem,
} from './local-storage-methods';
import { LSKey } from './local-storage.types';

type LocalStateHook<T> = [
  value: T,
  setValue: Dispatch<SetStateAction<T>>,
  getSyncValue: () => T
];
export default function useLocalState<
  D,
  T extends NotUndefined<D> = NotUndefined<D>
>(key: LSKey, defaultValue: T): LocalStateHook<T> {
  const stillLoadedRef = useComponentIsStillLoaded();
  const [value, setValue] = useState<T>(
    () => retrieveStoredItem<T>(key) ?? defaultValue
  );

  // callback functions use setValueSafe to avoid memory leaks in
  // components that have since un-mounted (while still setting localStorage)
  const setValueSafe: Dispatch<SetStateAction<T>> = useCallback(
    val => {
      if (stillLoadedRef()) setValue(val);
    },
    [stillLoadedRef]
  );

  const setItem = useCallback(
    (setStateAction: SetStateAction<T>) => {
      if (_.isFunction(setStateAction)) {
        const oldValue = retrieveStoredItem<T>(key) ?? defaultValue;
        const newValue = setStateAction(oldValue);
        storeItem<T>(key, newValue);
      } else {
        storeItem<T>(key, setStateAction);
      }
      window.dispatchEvent(new Event('storage'));
    },
    [defaultValue, key]
  );

  const handleStorage = useCallback(() => {
    const newItem = retrieveStoredItem<T>(key);
    if (!_.isEqual(newItem, value)) {
      setValueSafe(newItem ?? defaultValue);
    }
  }, [defaultValue, key, setValueSafe, value]);

  useEffect(() => {
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [handleStorage]);

  // NOTE: ensures that the synchronous value is always accessible
  const getSyncValue = useCallback(
    () => retrieveStoredItem<T>(key) ?? defaultValue,
    [defaultValue, key]
  );
  return [value, setItem, getSyncValue];
}
